import React, { useEffect, useState, useContext, useRef, useCallback } from "react";
import QRCode from "qrcode.react";
import toastError from "../../errors/toastError";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { Button, Dialog, DialogContent, DialogTitle, Paper, Typography } from "@material-ui/core";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import ColorModeContext from "../../layout/themeContext";
import { createGradientStyles } from "../../utils/themeColors";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    padding: theme.spacing(2),
  },
  dialogPaper: {
    minWidth: 320,
    maxWidth: 400,
    width: "100%",
    padding: theme.spacing(2),
    margin: 0,
    [theme.breakpoints.down("xs")]: {
      minWidth: "90vw",
    },
  },
  content: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    padding: theme.spacing(3),
  },
  qrCodeContainer: {
    padding: theme.spacing(3),
    backgroundColor: "white",
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(2),
  },
  loadingText: {
    marginTop: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
  // ✅ CORREÇÃO: Estilos do modal seguindo padrão do NewTicketModal
  dialogTitle: {
    padding: theme.spacing(2, 3),
    '& .MuiTypography-root': {
      fontWeight: 600,
      fontSize: '1.2rem'
    }
  },
  ...createGradientStyles(theme),
  dialogContentStyled: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.type === 'dark' ? theme.palette.background.default : '#fafafa'
  },
}));

const normalizeStatus = (status) => {
  if (!status) return "";
  const normalized = String(status).toLowerCase();
  if (normalized === "qrcode" || normalized === "qr" || normalized === "qr_code") return "qrcode";
  if (normalized === "connected") return "CONNECTED";
  if (normalized === "opening") return "OPENING";
  if (normalized === "restarting" || normalized === "reiniciando") return "REINICIANDO";
  if (normalized === "disconnected") return "DISCONNECTED";
  return status;
};

const QrcodeModal = ({ open, onClose, whatsAppId, requestFreshQrOnOpen = false, forceGenerateQrOnOpen = false }) => {
  const classes = useStyles();
  const theme = useTheme();
  const colorModeContext = useContext(ColorModeContext);
  const useGradient = colorModeContext?.useGradient || theme.palette?.gradient || false;
  const [qrCode, setQrCode] = useState("");
  const [status, setStatus] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const { user, socket } = useContext(AuthContext);
  const refreshRequestedRef = useRef(false);

  const applySessionState = useCallback((payload, source) => {
    const newStatus = normalizeStatus(payload?.status || "");
    const qrValue = payload?.qrcode || "";

    console.info("[QR_MODAL_FRONTEND_RECEIVED]", {
      source,
      whatsAppId,
      status: newStatus,
      hasQrcode: !!qrValue,
      qrLength: qrValue?.length || 0
    });

    if (newStatus === "CONNECTED") {
      setErrorMessage("");
      setQrCode("");
      setStatus(newStatus);
      onClose();
      return;
    }

    if (newStatus === "qrcode" && qrValue) {
      setErrorMessage("");
      setQrCode(qrValue);
      setStatus(newStatus);
      return;
    }

    if (newStatus === "REINICIANDO" || newStatus === "OPENING" || (newStatus === "qrcode" && !qrValue)) {
      setQrCode("");
      setStatus(newStatus);
      return;
    }

    if (newStatus === "DISCONNECTED") {
      setQrCode("");
      setStatus(newStatus);
      setErrorMessage("Nao foi possivel gerar o QR Code. Tente novamente.");
      return;
    }

    setQrCode("");
    setStatus(newStatus);
  }, [onClose, whatsAppId]);
  
  // ✅ Fechar modal quando status muda para CONNECTED
  useEffect(() => {
    if (status === "CONNECTED" && open) {
      console.log("[QRMODAL] Status changed to CONNECTED, closing modal");
      onClose();
    }
  }, [status, open, onClose]);

  const fetchSession = useCallback(async (source = "fetch") => {
    if (!whatsAppId) return;
    try {
      const { data } = await api.get(`/whatsapp/${whatsAppId}`);
      applySessionState(data, source);
    } catch (err) {
      if (err.response?.status !== 404) {
        console.error("[QRMODAL] Error fetching session:", err);
      }
    }
  }, [applySessionState, whatsAppId]);

  useEffect(() => {
    if (!open || !whatsAppId || !socket) {
      console.warn("[QRMODAL] Missing whatsAppId or socket", { whatsAppId, hasSocket: !!socket });
      return;
    }
    
    const companyId = user?.companyId;
    if (!companyId) {
      console.warn("[QRMODAL] Missing companyId", { user });
      return;
    }

    const eventName = `company-${companyId}-whatsappSession`;
    console.log("[QRMODAL] Registering listener", { eventName, whatsAppId, companyId, socketConnected: socket.connected });

    const onWhatsappData = (data) => {
      console.log("[QRMODAL] Event received", { 
        action: data.action, 
        sessionId: data.session?.id, 
        qrLength: data.session?.qrcode?.length,
        status: data.session?.status,
        qrcode: data.session?.qrcode
      });
      
      if (data.action === "update" && data.session.id === whatsAppId) {
        applySessionState(data.session, "socket");
      }
    };
    
    socket.on(eventName, onWhatsappData);
    console.log("[QRMODAL] Listener registered", { eventName });

    return () => {
      console.log("[QRMODAL] Cleaning up listener", { eventName });
      if (socket && typeof socket.off === 'function') {
        socket.off(eventName, onWhatsappData);
      }
    };
  }, [applySessionState, open, whatsAppId, user?.companyId, socket]);

  useEffect(() => {
    if (!open || !whatsAppId) {
      refreshRequestedRef.current = false;
      setErrorMessage("");
      return;
    }

    setErrorMessage("");
    fetchSession("initial_fetch");
  }, [open, whatsAppId, fetchSession]);

  useEffect(() => {
    if (!open || !whatsAppId || !requestFreshQrOnOpen || refreshRequestedRef.current) {
      return;
    }

    refreshRequestedRef.current = true;
    setStatus("OPENING");
    setQrCode("");
    setErrorMessage("");

    const requestFreshQr = async () => {
      try {
        await api.put(`/whatsappsession/${whatsAppId}`, { forceGenerateQr: forceGenerateQrOnOpen });
        fetchSession("post_refresh_request");
      } catch (err) {
        console.error("[QRMODAL] Error requesting new QR:", err);
        setErrorMessage(err?.response?.data?.error || "Nao foi possivel solicitar um novo QR Code.");
      }
    };

    requestFreshQr();
  }, [open, whatsAppId, requestFreshQrOnOpen, forceGenerateQrOnOpen, fetchSession]);

  useEffect(() => {
    if (!open || !whatsAppId) return undefined;

    const poll = setInterval(() => {
      if (!qrCode) {
        fetchSession("poll");
      }
    }, 3000);

    return () => clearInterval(poll);
  }, [open, whatsAppId, qrCode, fetchSession]);

  useEffect(() => {
    if (!open || !whatsAppId || qrCode) return undefined;

    const timeout = setTimeout(() => {
      setErrorMessage("O QR Code nao chegou a tempo. Voce pode tentar novamente.");
    }, 45000);

    return () => clearTimeout(timeout);
  }, [open, whatsAppId, qrCode]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      classes={{ paper: classes.dialogPaper }}
      aria-labelledby="qr-code-dialog"
    >
      <DialogTitle 
        className={`${classes.dialogTitle} ${useGradient ? classes.gradientBackground : classes.solidBackground}`}
      >
        {i18n.t("qrCode.title") || "Conectar WhatsApp"}
      </DialogTitle>
      <DialogContent className={`${classes.content} ${classes.dialogContentStyled}`}>
        <Paper elevation={0} className={classes.dialogPaper}>
          {status === "REINICIANDO" ? (
            <>
              <Typography variant="h6" color="primary" gutterBottom>
                Reiniciando conexão
              </Typography>
              <div className={classes.root}>
                <Typography variant="body1" className={classes.loadingText} style={{ marginTop: 16 }}>
                  A conexão está sendo reiniciada automaticamente. Aguarde alguns instantes...
                </Typography>
              </div>
            </>
          ) : (
            <>
              <Typography variant="h6" color="primary" gutterBottom>
                {i18n.t("qrCode.message")}
              </Typography>
              <div className={classes.root}>
                {/* ✅ CRÍTICO: Só exibir QR code se status for "qrcode" E QR code existir */}
                {status === "qrcode" && qrCode ? (
                  <div className={classes.qrCodeContainer}>
                    <QRCode 
                      value={qrCode} 
                      size={280} 
                      style={{ maxWidth: "100%", height: "auto" }}
                    />
                  </div>
                ) : (
                  <Typography variant="body1" className={classes.loadingText}>
                    {errorMessage || i18n.t("qrCode.waiting")}
                  </Typography>
                )}
                {errorMessage ? (
                  <Button
                    color="primary"
                    variant="outlined"
                    style={{ marginTop: 16 }}
                    onClick={async () => {
                      setErrorMessage("");
                      setStatus("OPENING");
                      setQrCode("");
                      try {
                        await api.put(`/whatsappsession/${whatsAppId}`, { forceGenerateQr: forceGenerateQrOnOpen });
                        fetchSession("manual_retry");
                      } catch (err) {
                        toastError(err);
                        setErrorMessage(err?.response?.data?.error || "Nao foi possivel gerar um novo QR Code.");
                      }
                    }}
                  >
                    Tentar novamente
                  </Button>
                ) : null}
              </div>
            </>
          )}
        </Paper>
      </DialogContent>
    </Dialog>
  );
};

export default React.memo(QrcodeModal);
